import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useVoucherStore } from '../stores/voucherStore';
import { useCampaignStore } from '../stores/campaignStore';
import { CampaignHeader } from '../components/CampaignHeader';
import { useAnalytics } from '../hooks/useAnalytics';

function formatCpfMasked(cpf?: string) {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.***.***.${digits.slice(9)}`;
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year}, às ${hours}h${minutes}`;
}

export function VoucherRedeemed() {
  const navigate = useNavigate();
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { customer, reservation } = useVoucherStore();
  const { campaign } = useCampaignStore();
  const { trackSelectContent } = useAnalytics();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <CampaignHeader />

      <main className="max-w-lg mx-auto w-full px-5 pt-8 pb-6 flex-1">
        {/* Success icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-rchlo-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={36} className="text-rchlo-green" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Brinde já resgatado</h1>
          <p className="text-sm text-gray-500 mt-2 max-w-xs mx-auto">
            Este CPF já utilizou o voucher desta campanha. Confira abaixo os detalhes do resgate realizado.
          </p>
        </div>

        {/* Voucher card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-rchlo-green uppercase tracking-wide">Voucher Consultado</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">{reservation?.voucherCode || '—'}</p>
            </div>
            <span className="px-3 py-1 text-xs font-bold text-orange-600 bg-orange-50 border border-orange-200 rounded-full uppercase">
              Resgatado
            </span>
          </div>

          {/* Redeemed date */}
          {reservation?.redeemedAt && (
            <div className="bg-amber-50/50 border border-amber-100 rounded-xl px-4 py-3">
              <p className="text-xs font-bold text-rchlo-green uppercase tracking-wide">Resgate efetuado em</p>
              <p className="text-base font-bold text-rchlo-green mt-0.5">
                {formatDateTime(reservation.redeemedAt)}
              </p>
            </div>
          )}

          {/* CPF */}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">CPF</p>
            <p className="text-base font-medium text-gray-900">{formatCpfMasked(customer?.cpf)}</p>
          </div>

          {/* Product */}
          {campaign && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Brinde</p>
              <p className="text-base font-medium text-gray-900">{campaign.productName || campaign.name}</p>
            </div>
          )}

          {/* Store */}
          {reservation?.storeName && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Loja de Resgate</p>
              <p className="text-base font-medium text-gray-900">{reservation.storeName}</p>
              {reservation.storeCity && (
                <p className="text-sm text-gray-500">{reservation.storeCity}{reservation.storeState ? ` • ${reservation.storeState}` : ''}</p>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-lg mx-auto w-full px-5 pb-6">
        <button
          onClick={() => {
            trackSelectContent('button', 'voltar_inicio');
            navigate(`/${campaignSlug}`);
          }}
          className="btn-primary w-full uppercase tracking-wide font-bold"
        >
          Voltar ao Início
        </button>
      </footer>
    </div>
  );
}
